import { type ReactNode, useState } from "react";
import { PlusIcon } from "lucide-react";

import {
  ConfirmationDialog,
  DisabledExplanationTooltip,
  Row,
} from "@/components";
import { Button } from "@/components/ui/button";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

export const IntakeTabBar = ({
  addLabel,
  children,
  deleteDescription,
  onAdd,
  onDelete,
  removeButtonProps,
  screenName,
  trackingName,
}: {
  addLabel: string;
  children: ReactNode;
  deleteDescription: string;
  onAdd: () => void;
  onDelete: () => void;
  removeButtonProps?: { disabled: boolean; message: string };
  screenName: MIXPANEL_SCREENS;
  trackingName?: string;
}) => {
  const entityName = trackingName ?? addLabel.replace(/^Add\s+/i, "");
  const trackedAddLabel = `Add ${entityName}`;
  const trackedRemoveLabel = `Remove ${entityName}`;

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const render = () => (
    <Row className="items-center border-b border-slate-200">
      <div className="relative min-w-0 flex-1">
        <Row className="no-scrollbar items-center overflow-x-auto">
          {children}
        </Row>
        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      </div>
      <Row className="ml-auto shrink-0 items-center gap-2 pl-4">
        <Button
          className="h-8 shrink-0 gap-1.5 px-3 text-xs"
          onClick={() => {
            mixpanelUtils.trackButtonClick(trackedAddLabel, screenName);
            onAdd();
          }}
          size="sm"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {addLabel}
        </Button>
        <DisabledExplanationTooltip
          disabled={removeButtonProps?.disabled ?? false}
          message={removeButtonProps?.message ?? ""}
        >
          <Button
            className="h-8 border-red-300 px-3 text-xs"
            disabled={removeButtonProps?.disabled ?? false}
            onClick={() => {
              setIsDeleteDialogOpen(true);
            }}
            variant="outlineDestructive"
          >
            Remove
          </Button>
        </DisabledExplanationTooltip>
      </Row>
      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description={deleteDescription}
        onConfirm={() => {
          mixpanelUtils.trackButtonClick(trackedRemoveLabel, screenName);
          setIsDeleteDialogOpen(false);
          onDelete();
        }}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title="Are you sure?"
      />
    </Row>
  );

  return render();
};
